import type { ImageAnalyses } from '../../../@types/latestImagesV2';
import type {
  TClaimNote,
  TDmfValue,
  TFlattenedTreatment,
} from '../../../@types/rcm';
import type { InsuranceView } from '../../insuranceExport/constants';

import DevTool from './devTool';
import type { FilteredImage } from './hooks/useFilteredImages';
import { ImagesView } from './ImagesView';
import { LoadingSkeleton } from './LoadingSkeleton';
import NarrativeDevTool from './narrativeDevTool';
import type { TGetNarrativeDev } from './narrativeDevTool/useGetGptResponse';
import type { TImageSets, TIncludeTypes, TPatientInfo } from './state';
import { SummaryConfig } from './summaryConfig/SummaryConfig';

type SummaryPageProps = {
  claimNotes: TClaimNote[] | undefined;
  clinicalNotes: string;
  defaultTeethList: string[];
  dmfValues: TDmfValue[] | undefined;
  estimatedVisitId: string | null;
  unifiedAppointmentId: number | undefined;
  filteredImages: FilteredImage[];
  filteredTeethList: string[];
  images: ImageAnalyses[];
  imageSetGroup: TImageSets;
  includeFindingTypes: TIncludeTypes;
  insPage: InsuranceView;
  narrativeText: string;
  narrativeDevToolData: TGetNarrativeDev | undefined;
  patientInfo: TPatientInfo;
  pmsAppointmentId: string | undefined;
  radiographsTakenDateFormatted: string;
  selectedTreatments: {
    cdtCode: string;
    quadrant: 'UR' | 'UL' | 'LR' | 'LL' | undefined;
  }[];
  supportedTreatmentType: 'SRP' | null;
  treatments: TFlattenedTreatment[];
  treatmentsToUse: TFlattenedTreatment[];
  visitDate: string;
  isLoading: boolean;
  isPaginated: boolean;
  isSrp: boolean;
  onSelectTreatments: (txIds: string[]) => void;
  onSelectBitewingOnly: (isBitewingOnly: boolean) => void;
};

// TODO: Clean up prop list, group and spread props
export const SummaryPage = ({
  claimNotes,
  clinicalNotes,
  defaultTeethList,
  dmfValues,
  estimatedVisitId,
  unifiedAppointmentId,
  filteredImages,
  filteredTeethList,
  images,
  imageSetGroup,
  includeFindingTypes,
  insPage,
  narrativeText,
  narrativeDevToolData,
  patientInfo,
  pmsAppointmentId,
  radiographsTakenDateFormatted,
  selectedTreatments,
  supportedTreatmentType,
  treatments,
  treatmentsToUse,
  visitDate,
  isLoading,
  isPaginated,
  isSrp,
  onSelectBitewingOnly,
  onSelectTreatments,
}: SummaryPageProps) => {
  const imagesView = (
    <ImagesView
      images={filteredImages}
      estimatedVisitId={Number(estimatedVisitId)}
      imageBw={includeFindingTypes.includeBitewingsOnly}
      imageFindingsResto={includeFindingTypes.includeCariousLesions}
      imageFindingsDmf={includeFindingTypes.includeDmf}
      imageFindingsPerio={includeFindingTypes.includeRadiographicBoneLoss}
      imageFindingsEndo={includeFindingTypes.includePeriapicalRadiolucency}
      includeRadiographsTakenDate={
        includeFindingTypes.includeRadiographsTakenDate
      }
      dmfValues={dmfValues ?? []}
      pmsAppointmentId={pmsAppointmentId}
      selectedTreatments={selectedTreatments}
      supportedTreatmentType={supportedTreatmentType}
      radiographsTakenDateFormatted={radiographsTakenDateFormatted}
      narrativeText={narrativeText}
      imageSetGroup={imageSetGroup}
      clinicalNotes={clinicalNotes}
      patientName={patientInfo.name}
      dob={patientInfo.dob}
      visitDate={visitDate}
      hidePMSInformation={false}
      isPaginated={isPaginated}
    />
  );

  const pageComponents = {
    loading: <LoadingSkeleton />,
    'dev-tool': (
      <DevTool
        images={images}
        filteredImages={filteredImages}
        treatments={treatments}
        filteredTreatments={treatmentsToUse}
      />
    ),
    'narrative-dev-tool': (
      <NarrativeDevTool
        defaultUserInput={narrativeDevToolData?.input ?? ''}
        defaultSystemPrompt={narrativeDevToolData?.prompt ?? ''}
        defaultModel={narrativeDevToolData?.model ?? 'gpt-4o'}
        defaultTemperature={narrativeDevToolData?.temperature ?? 0}
        defaultTopP={narrativeDevToolData?.top_p ?? 1}
        defaultFrequencyPenalty={narrativeDevToolData?.frequency_penalty ?? 0}
        defaultPresencePenalty={narrativeDevToolData?.presence_penalty ?? 0}
        narrativeResponse={narrativeText}
      />
    ),
    'preview-single': imagesView,
    'preview-pages': imagesView,
    config: (
      <SummaryConfig
        defaultTeethList={defaultTeethList}
        filteredTeethList={filteredTeethList}
        images={filteredImages}
        claimNotes={claimNotes ?? []}
        estimatedVisitId={Number(estimatedVisitId)}
        unifiedAppointmentId={unifiedAppointmentId}
        defaultTreatments={treatments ?? []}
        treatmentsToUse={treatmentsToUse}
        radiographsTakenDate={radiographsTakenDateFormatted}
        isSrp={isSrp}
        onSelectTreatments={onSelectTreatments}
        onSelectBitewingOnly={onSelectBitewingOnly}
      />
    ),
  };

  if (isLoading) return pageComponents.loading;
  return pageComponents[insPage] || null;
};
